import { buildWebStorage, setupCache } from 'axios-cache-interceptor';
import { getActiveSupportContext } from '../mixins/CurrentUserMixin';
import { createAxiosInstance } from '../vue_plugins/axios';
const userSupportContext = getActiveSupportContext();
export const FRONTEND_CACHE_PREFIX = `cosmic_${userSupportContext}_`;
// minimum Time To Live (this is overridable for each request)
const TTL = 1000 * 20; // 20s
const clearCacheByKeys = (keys) => {
    for (const key of keys) {
        localStorage.removeItem(key);
    }
};
const createDalAxiosInstance = () => {
    const axiosInstanceWithCaching = setupCache(createAxiosInstance(), {
        storage: buildWebStorage(localStorage, FRONTEND_CACHE_PREFIX),
        ttl: TTL
    });
    return axiosInstanceWithCaching;
};
const findMatchingKeysToSpoil = (prefix) => {
    const matchingKeys = [];
    // Loop through all keys in localStorage
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        // Check if the key starts with the specified prefix
        if (key && key.startsWith(`${FRONTEND_CACHE_PREFIX}${prefix}`)) {
            matchingKeys.push(key);
        }
    }
    return matchingKeys.length
        ? matchingKeys.reduce((obj, key) => {
            obj[key] = 'delete';
            return obj;
        }, {})
        : null;
};
// #region Activities
const ACTIVITY_COLLECTION_CACHE_KEY = 'collection_activities';
const ACTIVITY_URL_SLUG = `activities`;
export const clearActivityCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(ACTIVITY_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    clearCacheByKeys(Object.keys(keys));
};
export const clearActivityCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const getActivities = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${ACTIVITY_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${ACTIVITY_URL_SLUG}`, {
        id,
        params
    });
};
export const getActivity = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${ACTIVITY_URL_SLUG}/${id}`, {
        id
    });
};
export const putActivity = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ACTIVITY_COLLECTION_CACHE_KEY);
    return http.put(`/${ACTIVITY_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Announcements
const ANNOUNCEMENT_COLLECTION_CACHE_KEY = 'collection_announcements';
const ANNOUNCEMENT_URL_SLUG = `announcements`;
export const clearAnnouncementsCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(ANNOUNCEMENT_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    clearCacheByKeys(Object.keys(keys));
};
export const deleteAnnouncement = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ANNOUNCEMENT_COLLECTION_CACHE_KEY);
    return http.delete(`/${ANNOUNCEMENT_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getAnnouncements = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${ANNOUNCEMENT_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${ANNOUNCEMENT_URL_SLUG}`, {
        id,
        params
    });
};
export const postAnnouncement = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ANNOUNCEMENT_COLLECTION_CACHE_KEY);
    const update = {
        ...spoilers
    };
    return http.post(`/${ANNOUNCEMENT_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update
        }
    });
};
export const putAnnouncement = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ANNOUNCEMENT_COLLECTION_CACHE_KEY);
    return http.put(`/${ANNOUNCEMENT_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Assembly templates
const ASSEMBLY_TEMPLATE_COLLECTION_CACHE_KEY = 'collection_assembly_templates';
const ASSEMBLY_TEMPLATE_URL_SLUG = `assembly-templates`;
export const clearAssemblyTemplateCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(ASSEMBLY_TEMPLATE_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    clearCacheByKeys(Object.keys(keys));
};
export const clearAssemblyTemplateCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const deleteAssemblyTemplate = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ASSEMBLY_TEMPLATE_COLLECTION_CACHE_KEY);
    return http.delete(`/${ASSEMBLY_TEMPLATE_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getAssemblyTemplates = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${ASSEMBLY_TEMPLATE_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${ASSEMBLY_TEMPLATE_URL_SLUG}`, {
        id,
        params
    });
};
export const getAssemblyTemplate = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${ASSEMBLY_TEMPLATE_URL_SLUG}/${id}`, {
        id
    });
};
export const postAssemblyTemplate = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ASSEMBLY_TEMPLATE_COLLECTION_CACHE_KEY);
    const update = {
        ...spoilers
    };
    if (body.parentId) {
        update[body.parentId] = 'delete';
    }
    return http.post(`/${ASSEMBLY_TEMPLATE_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update
        }
    });
};
export const putAssemblyTemplate = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ASSEMBLY_TEMPLATE_COLLECTION_CACHE_KEY);
    return http.put(`/${ASSEMBLY_TEMPLATE_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Approval Routing Templates (ARTs)
const APPROVAL_ROUTING_TEMPLATE_COLLECTION_CACHE_KEY = 'collection_approval_routing_templates';
const APPROVAL_ROUTING_TEMPLATE_URL_SLUG = `change-sets/approval-templates`;
export const clearApprovalRoutingTemplateCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(APPROVAL_ROUTING_TEMPLATE_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const deleteApprovalRoutingTemplate = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(APPROVAL_ROUTING_TEMPLATE_COLLECTION_CACHE_KEY);
    return http.delete(`/${APPROVAL_ROUTING_TEMPLATE_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getApprovalRoutingTemplates = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${APPROVAL_ROUTING_TEMPLATE_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${APPROVAL_ROUTING_TEMPLATE_URL_SLUG}`, {
        id,
        params
    });
};
export const getApprovalRoutingTemplate = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${APPROVAL_ROUTING_TEMPLATE_URL_SLUG}/${id}`, {
        id
    });
};
export const postApprovalRoutingTemplate = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(APPROVAL_ROUTING_TEMPLATE_COLLECTION_CACHE_KEY);
    return http.post(`/${APPROVAL_ROUTING_TEMPLATE_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putApprovalRoutingTemplate = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(APPROVAL_ROUTING_TEMPLATE_COLLECTION_CACHE_KEY);
    return http.put(`/${APPROVAL_ROUTING_TEMPLATE_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Attachments
const ATTACHMENTS_COLLECTION_CACHE_KEY = 'collection_attachments';
const ATTACHMENTS_URL_SLUG = `attachments`;
export const clearAttachmentsCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(ATTACHMENTS_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    clearCacheByKeys(Object.keys(keys));
};
export const deleteAttachment = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ATTACHMENTS_COLLECTION_CACHE_KEY);
    return http.delete(`/${ATTACHMENTS_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getAS2DownloadUrl = (id, fileName) => `/api2/attachments/download/${id}/${fileName}`;
export const getAttachment = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${ATTACHMENTS_URL_SLUG}/${id}`, {
        id
    });
};
export const getAttachmentDownload = async (attachment) => {
    const url = getAS2DownloadUrl(attachment.id, attachment.fileName || '');
    const currentUserActiveSupportContext = getActiveSupportContext();
    const headers = new Headers();
    headers.append('x-organization', currentUserActiveSupportContext || '');
    const resp = await fetch(url, { headers });
    const blob = await resp.blob();
    return new File([blob], '', { type: attachment.contentType || 'image/png' });
};
export const getAttachments = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${ATTACHMENTS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${ATTACHMENTS_URL_SLUG}`, {
        id,
        params
    });
};
export const postAttachment = (attachmentData) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ATTACHMENTS_COLLECTION_CACHE_KEY);
    const formData = new FormData();
    formData.append('file', attachmentData.file);
    formData.append('description', attachmentData.description);
    formData.append('subType', attachmentData.type);
    return http.post(`/${ATTACHMENTS_URL_SLUG}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'x-organization': userSupportContext || ''
        },
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putAttachment = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ATTACHMENTS_COLLECTION_CACHE_KEY);
    return http.put(`/${ATTACHMENTS_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Auto-hardware lists
const AUTO_HARDWARE_LIST_COLLECTION_CACHE_KEY = 'collection_auto_hardware_lists';
const AUTO_HARDWARE_LIST_URL_SLUG = `auto-hardware-lists`;
export const deleteAutoHardwareList = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(AUTO_HARDWARE_LIST_COLLECTION_CACHE_KEY);
    return http.delete(`/${AUTO_HARDWARE_LIST_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getAutoHardwareList = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${AUTO_HARDWARE_LIST_URL_SLUG}/${id}`, {
        id
    });
};
export const getAutoHardwareLists = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${AUTO_HARDWARE_LIST_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${AUTO_HARDWARE_LIST_URL_SLUG}`, {
        id,
        params
    });
};
export const postAutoHardwareList = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(AUTO_HARDWARE_LIST_COLLECTION_CACHE_KEY);
    return http.post(`/${AUTO_HARDWARE_LIST_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putAutoHardwareList = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(AUTO_HARDWARE_LIST_COLLECTION_CACHE_KEY);
    return http.put(`/${AUTO_HARDWARE_LIST_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Comments
const COMMENTS_COLLECTION_CACHE_KEY = 'collection_comments';
const COMMENTS_URL_SLUG = `comments`;
export const clearCommentsCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(COMMENTS_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    clearCacheByKeys(Object.keys(keys));
};
export const getComments = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${COMMENTS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${COMMENTS_URL_SLUG}`, {
        id,
        params
    });
};
export const getCommentById = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${COMMENTS_URL_SLUG}/${id}`, {
        id
    });
};
export const postComment = (params, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(COMMENTS_COLLECTION_CACHE_KEY);
    return http.post(`/${COMMENTS_URL_SLUG}`, {
        ...body
    }, {
        params,
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Contacts
const CONTACTS_COLLECTION_CACHE_KEY = 'collection_contacts';
const CONTACTS_URL_SLUG = `contacts`;
export const clearContactCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const deleteContact = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(CONTACTS_COLLECTION_CACHE_KEY);
    return http.delete(`/${CONTACTS_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getContact = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${CONTACTS_URL_SLUG}/${id}`, {
        id
    });
};
export const getContacts = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${CONTACTS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${CONTACTS_URL_SLUG}`, {
        id,
        params,
        cache: {
            ttl: TTL * 10
        }
    });
};
export const postContact = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(CONTACTS_COLLECTION_CACHE_KEY);
    return http.post(`/${CONTACTS_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putContact = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(CONTACTS_COLLECTION_CACHE_KEY);
    return http.put(`/${CONTACTS_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Contracts
const CONTRACTS_COLLECTION_CACHE_KEY = 'collection_contracts';
const CONTRACTS_URL_SLUG = `contracts`;
export const clearContractCacheByContractNumber = (contractNumber) => {
    if (!contractNumber) {
        return;
    }
    localStorage.removeItem(contractNumber);
};
export const clearContractCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(CONTRACTS_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    clearCacheByKeys(Object.keys(keys));
};
export const getContract = (contractNumber) => {
    const http = createDalAxiosInstance();
    return http.get(`/${CONTRACTS_URL_SLUG}/${encodeURIComponent(contractNumber)}`, {
        id: contractNumber
    });
};
export const getContracts = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${CONTRACTS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${CONTRACTS_URL_SLUG}`, {
        id,
        params
    });
};
// #endregion
// #region ChangeLogs
const CHANGE_LOGS_COLLECTION_CACHE_KEY = 'collection_change_logs';
const CHANGE_LOGS_URL_SLUG = `change-logs`;
export const clearChangeLogCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(CHANGE_LOGS_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const getChangeLog = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${CHANGE_LOGS_URL_SLUG}/${id}`, {
        id
    });
};
export const getChangeLogs = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${CHANGE_LOGS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${CHANGE_LOGS_URL_SLUG}`, {
        id,
        params
    });
};
// #endregion
// #region ChangeSets
const CHANGE_SETS_COLLECTION_CACHE_KEY = 'collection_change_sets';
const CHANGE_SETS_URL_SLUG = `change-sets`;
export const clearChangeSetCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(CHANGE_SETS_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const clearChangeSetCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const getChangeSet = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${CHANGE_SETS_URL_SLUG}/${id}`, {
        id
    });
};
export const getChangeSets = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${CHANGE_SETS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${CHANGE_SETS_URL_SLUG}`, {
        id,
        params
    });
};
export const getChangeSetsUpcoming = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${CHANGE_SETS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${CHANGE_SETS_URL_SLUG}/upcoming`, {
        id,
        params
    });
};
export const putChangeSetAuthority = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(CHANGE_SETS_COLLECTION_CACHE_KEY);
    return http.put(`/${CHANGE_SETS_URL_SLUG}/authorities/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Delivery orders
const DELIVERY_ORDER_COLLECTION_CACHE_KEY = 'collection_delivery_orders';
const DELIVERY_ORDER_URL_SLUG = `documents/delivery-orders`;
export const clearDeliveryOrderCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(DELIVERY_ORDER_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const clearDeliveryOrderCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const deleteDeliveryOrder = (deliveryOrderId) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DELIVERY_ORDER_COLLECTION_CACHE_KEY);
    return http.delete(`/documents/${deliveryOrderId}`, {
        cache: {
            update: {
                [`${deliveryOrderId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const deleteDeliveryOrderLineItem = (deliveryOrderId, params) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DELIVERY_ORDER_COLLECTION_CACHE_KEY);
    return http.delete(`/${DELIVERY_ORDER_URL_SLUG}/${deliveryOrderId}/line-items`, {
        params,
        cache: {
            update: {
                [`${deliveryOrderId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getDeliveryOrder = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${DELIVERY_ORDER_URL_SLUG}/${id}`, {
        id
    });
};
export const getDeliveryOrders = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${DELIVERY_ORDER_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${DELIVERY_ORDER_URL_SLUG}`, {
        id,
        params
    });
};
export const getDeliveryOrderLineItems = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${DELIVERY_ORDER_COLLECTION_CACHE_KEY}_line_items_${customKeyWithParams}`;
    return http.get(`/${DELIVERY_ORDER_URL_SLUG}/line-items`, {
        id,
        params
    });
};
export const postDeliveryOrderLineItem = (deliveryOrderId, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DELIVERY_ORDER_COLLECTION_CACHE_KEY);
    return http.post(`/${DELIVERY_ORDER_URL_SLUG}/${deliveryOrderId}/line-items`, {
        ...body
    }, {
        cache: {
            update: {
                [`${deliveryOrderId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const putDeliveryOrderLineItem = (deliveryOrderId, // document id
lineItemId, // change_set id
body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DELIVERY_ORDER_COLLECTION_CACHE_KEY);
    return http.put(`/${DELIVERY_ORDER_URL_SLUG}/${deliveryOrderId}/line-items/${lineItemId}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${deliveryOrderId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const closeDeliveryOrderLineItems = (deliveryOrderId, // document id
body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DELIVERY_ORDER_COLLECTION_CACHE_KEY);
    return http.put(`/${DELIVERY_ORDER_URL_SLUG}/${deliveryOrderId}/line-items/close`, {
        ...body
    }, {
        cache: {
            update: {
                [`${deliveryOrderId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const putDeliveryOrder = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DELIVERY_ORDER_COLLECTION_CACHE_KEY);
    return http.put(`/${DELIVERY_ORDER_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Documents
const DOCUMENT_COLLECTION_CACHE_KEY = 'collection_documents';
const DOCUMENT_URL_SLUG = `documents`;
export const clearDocumentCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(DOCUMENT_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const clearDocumentCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const deleteDocument = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DOCUMENT_COLLECTION_CACHE_KEY);
    return http.delete(`/documents/${id}`, {
        cache: {
            update: {
                [`${DOCUMENT_COLLECTION_CACHE_KEY}`]: 'delete',
                [`${id}`]: 'delete',
                [`docs_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const deleteDocumentLineItem = (id, params) => {
    const http = createDalAxiosInstance();
    return http.delete(`/documents/${id}/line-items`, {
        params,
        cache: {
            update: {
                [`${id}`]: 'delete'
            }
        }
    });
};
export const deleteDocumentRelationship = (id, params) => {
    const http = createDalAxiosInstance();
    return http.delete(`/documents/${id}/relationships`, {
        data: {
            ...params
        },
        cache: {
            update: {
                [`relationships_${id}`]: 'delete'
            }
        }
    });
};
export const getDocument = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${DOCUMENT_URL_SLUG}/${id}`, {
        id: `docs_${id}`
    });
};
export const getDocuments = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${DOCUMENT_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${DOCUMENT_URL_SLUG}`, {
        id,
        params
    });
};
export const getDocumentsHistory = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${DOCUMENT_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${DOCUMENT_URL_SLUG}/history`, {
        id,
        params
    });
};
export const getDocumentOverviewStats = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${DOCUMENT_COLLECTION_CACHE_KEY}_overview_${customKeyWithParams}`;
    return http.get(`/${DOCUMENT_URL_SLUG}/report/overview`, {
        id,
        params
    });
};
export const getDocumentRelationships = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${DOCUMENT_URL_SLUG}/${id}/relationships`, {
        id: `relationships_${id}`
    });
};
export const getDocumentStakeholders = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${DOCUMENT_URL_SLUG}/${id}/stakeholders`, {
        id: `stakeholders_${id}`
    });
};
export const postDocument = (body) => {
    const http = createDalAxiosInstance();
    return http.post(`/${DOCUMENT_URL_SLUG}`, {
        ...body
    });
};
export const postDocumentExists = (body) => {
    const http = createDalAxiosInstance();
    return http.post(`/${DOCUMENT_URL_SLUG}/exists`, {
        ...body
    });
};
export const postDocumentAttachment = (documentId, attachmentId) => {
    const http = createDalAxiosInstance();
    // swagger says it returns nothing but that makes the response not happy
    return http.post(`/${DOCUMENT_URL_SLUG}/${documentId}/attachments/${attachmentId}`, {});
};
export const postDocumentClosure = (documentId, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DOCUMENT_COLLECTION_CACHE_KEY);
    return http.post(`/documents/${documentId}/closure`, body, {
        cache: {
            update: {
                [`docs_${documentId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const postDocumentLineItems = (id, body) => {
    const http = createDalAxiosInstance();
    return http.post(`/${DOCUMENT_URL_SLUG}/${id}/line-items`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete'
            }
        }
    });
};
export const postDocumentRelationships = (id, body) => {
    const http = createDalAxiosInstance();
    return http.post(`/${DOCUMENT_URL_SLUG}/${id}/relationships`, {
        ...body
    }, {
        cache: {
            update: {
                [`relationships_${id}`]: 'delete'
            }
        }
    });
};
export const putDocument = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DOCUMENT_COLLECTION_CACHE_KEY);
    return http.put(`/${DOCUMENT_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`docs_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const putDocumentLineItemsClose = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DOCUMENT_COLLECTION_CACHE_KEY);
    return http.put(`/${DOCUMENT_URL_SLUG}/${id}/line-items/close`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const putDocumentLineItemReopen = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(DOCUMENT_COLLECTION_CACHE_KEY);
    return http.put(`/${DOCUMENT_URL_SLUG}/${id}/line-items/reopen`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// export const putDocumentLineItem = (id: string, changeSetId: number, body: CosmicLineItemUpdateRequest) => {
//   const http = createDalAxiosInstance();
//   const spoilers = findMatchingKeysToSpoil(DOCUMENT_COLLECTION_CACHE_KEY);
//   return http.put<CosmicLineItemResponse>(
//     `/${DOCUMENT_URL_SLUG}/${id}/cosmic-line-item/${changeSetId}`,
//     {
//       ...body
//     },
//     {
//       cache: {
//         update: {
//           [`${id}`]: 'delete',
//           ...spoilers
//         }
//       }
//     } as any
//   );
// };
export const getDocumentFormFields = (documentId) => {
    const http = createDalAxiosInstance();
    return http.get(`/${DOCUMENT_URL_SLUG}/${documentId}/work-order-step-form-fields`, {
        id: `form_fields_${documentId}`
    });
};
// #endregion
// #region Document attachments
const DOCUMENT_ATTACHMENTS_CACHE_PREFIX = 'document_attachments';
export const clearDocumentAttachmentsCacheById = (docId) => {
    if (!docId) {
        return;
    }
    // should only be one key per id, but just in case
    const keys = findMatchingKeysToSpoil(`${DOCUMENT_ATTACHMENTS_CACHE_PREFIX}_${docId}`);
    if (!keys) {
        return;
    }
    clearCacheByKeys(Object.keys(keys));
};
export const getDocumentAttachments = (docId) => {
    const http = createDalAxiosInstance();
    return http.get(`/documents/${docId}/attachments`, {
        id: `${DOCUMENT_ATTACHMENTS_CACHE_PREFIX}_${docId}`
    });
};
export const linkDocumentAttachment = (docId, attachmentId) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(`${DOCUMENT_ATTACHMENTS_CACHE_PREFIX}_${docId}`);
    return http.post(`/documents/${docId}/attachments/${attachmentId}`, null, {
        cache: {
            update: {
                [`${DOCUMENT_ATTACHMENTS_CACHE_PREFIX}_${docId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const unlinkDocumentAttachment = (id, attachmentId) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(`${DOCUMENT_ATTACHMENTS_CACHE_PREFIX}_${id}`);
    return http.delete(`/documents/${id}/attachments/${attachmentId}`, {
        cache: {
            update: {
                [`${DOCUMENT_ATTACHMENTS_CACHE_PREFIX}_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Events
const EVENTS_COLLECTION_CACHE_KEY = 'collection_events';
const EVENTS_URL_SLUG = `cosmic-events`;
export const clearEventCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const deleteEvent = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(EVENTS_COLLECTION_CACHE_KEY);
    return http.delete(`/${EVENTS_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getEvent = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${EVENTS_URL_SLUG}/${id}`, {
        id: `${id}`
    });
};
export const getEvents = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${EVENTS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${EVENTS_URL_SLUG}`, {
        id,
        params
    });
};
export const postEvent = (body) => {
    const http = createDalAxiosInstance();
    return http.post(`/${EVENTS_URL_SLUG}`, {
        ...body
    });
};
export const putEvent = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(EVENTS_COLLECTION_CACHE_KEY);
    return http.put(`/${EVENTS_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Hardware lists
const HARDWARE_LIST_COLLECTION_CACHE_KEY = 'collection_hardware_lists';
const HARDWARE_LIST_URL_SLUG = `hardware-lists`;
export const clearHardwareListCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const clearHardwareListCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(HARDWARE_LIST_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const getHardwareList = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${HARDWARE_LIST_URL_SLUG}/${id}`, {
        id
    });
};
export const getActivityReportByHardwareListId = (id) => {
    const http = createDalAxiosInstance();
    const cacheId = `ACTIVITY_REPORT_${id}`;
    return http.get(`/${HARDWARE_LIST_URL_SLUG}/report/${id}/activities`, {
        id: cacheId
    });
};
export const getHardwareLists = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${HARDWARE_LIST_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${HARDWARE_LIST_URL_SLUG}`, {
        id,
        params
    });
};
export const getHardwareListPartSnapshots = (hardwareListId) => {
    const http = createDalAxiosInstance();
    return http.get(`/${HARDWARE_LIST_URL_SLUG}/${hardwareListId}/part-snapshots`, {
        id: `part_snapshots_${hardwareListId}`
    });
};
export const putHardwareList = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(HARDWARE_LIST_COLLECTION_CACHE_KEY);
    return http.put(`/${HARDWARE_LIST_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Hardware search
const HARDWARE_SEARCH_COLLECTION_CACHE_KEY = 'collection_hardware_search';
const HARDWARE_SEARCH_URL_SLUG = `hardware`;
export const getAllHardware = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${HARDWARE_SEARCH_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${HARDWARE_SEARCH_URL_SLUG}`, {
        id,
        params
    });
};
// #endregion
// #region Inventories
const INVENTORY_COLLECTION_CACHE_KEY = 'collection_inventories';
export const clearInventoryCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(INVENTORY_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    clearCacheByKeys(Object.keys(keys));
};
export const getInventory = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/inventory/${id}`, {
        id
    });
};
export const clearInventoryAssemblyCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + `inv_assy_${id}`);
};
export const getInventoryAssembly = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/inventory/assembly/${id}`, {
        id: `inv_assy_${id}`
    });
};
export const getInventories = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${INVENTORY_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/hardware/inventory`, {
        id,
        params
    });
};
export const getInventoriesIssued = (params) => {
    const http = createDalAxiosInstance();
    // const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    // const id = `${INVENTORY_COLLECTION_CACHE_KEY}issued_${customKeyWithParams}`;
    return http.get(`/inventory/report/issued`, {
        // id,
        params
    });
};
export const issueInventory = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(INVENTORY_COLLECTION_CACHE_KEY);
    return http.post(`/inventory/issue`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const issuedInventory = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(INVENTORY_COLLECTION_CACHE_KEY);
    return http.post(`/inventory/issued`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const receiveInventory = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(INVENTORY_COLLECTION_CACHE_KEY);
    return http.post(`/inventory/receipt`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const transferInventory = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(INVENTORY_COLLECTION_CACHE_KEY);
    return http.post(`/inventory/transfer`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Hardware attachments
export const getItemDrawingAttachments = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = btoa(JSON.stringify(params));
    const id = `${ATTACHMENTS_COLLECTION_CACHE_KEY}_item_drawing_${customKeyWithParams}`;
    return http.get(`/attachments/item-drawings/${params.drawingNumber}`, {
        id,
        params
    });
};
export const getItemMasterAttachments = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = btoa(JSON.stringify(params));
    const id = `${ATTACHMENTS_COLLECTION_CACHE_KEY}_item_master_${customKeyWithParams}`;
    return http.get(`/attachments/item-masters/${params.drawingNumber}/${params.asBuiltNumber}/${params.side}`, {
        id,
        params
    });
};
export const getItemInstanceAttachments = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = btoa(JSON.stringify(params));
    const id = `${ATTACHMENTS_COLLECTION_CACHE_KEY}_item_instance_${customKeyWithParams}`;
    return http.get(`/attachments/item-instances/${params.id}`, {
        id,
        params
    });
};
export const postItemDrawingAttachments = (drawingNumber, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ATTACHMENTS_COLLECTION_CACHE_KEY);
    return http.post(`/attachments/item-drawings/${drawingNumber}`, body, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const postItemMasterAttachments = (drawingNumber, asBuiltNumber, side, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ATTACHMENTS_COLLECTION_CACHE_KEY);
    return http.post(`/attachments/item-masters/${drawingNumber}/${asBuiltNumber}/${side}`, body, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const postItemInstanceAttachments = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ATTACHMENTS_COLLECTION_CACHE_KEY);
    return http.post(`/attachments/item-instances/${id}`, body, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Item instances
const ITEM_INSTANCE_COLLECTION_CACHE_KEY = 'collection_item_instances';
const ITEM_INSTANCE_URL_SLUG = `hardware/item-instances`;
export const clearItemInstanceCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const getItemInstance = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${ITEM_INSTANCE_URL_SLUG}/${id}`, {
        id
    });
};
export const getItemInstances = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${ITEM_INSTANCE_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${ITEM_INSTANCE_URL_SLUG}`, {
        id,
        params
    });
};
export const putItemInstance = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ITEM_INSTANCE_COLLECTION_CACHE_KEY);
    return http.put(`/${ITEM_INSTANCE_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Locations
const LOCATION_COLLECTION_CACHE_KEY = 'collection_locations';
const LOCATION_URL_SLUG = `locations`;
export const deleteLocation = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(LOCATION_COLLECTION_CACHE_KEY);
    return http.delete(`/${LOCATION_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getLocation = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${LOCATION_URL_SLUG}/${id}`, {
        id
    });
};
export const getLocations = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${LOCATION_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${LOCATION_URL_SLUG}`, {
        id,
        params,
        cache: {
            ttl: TTL * 10
        }
    });
};
export const putLocation = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(LOCATION_COLLECTION_CACHE_KEY);
    return http.put(`/${LOCATION_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Lookups
export const clearProjectCodeCollectionCache = () => {
    const keys = findMatchingKeysToSpoil('project_codes_');
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const getProjectCodes = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `project_codes_${customKeyWithParams}`;
    return http.get(`/project-codes`, {
        id,
        params,
        cache: {
            ttl: TTL * 10
        }
    });
};
// #endregion
// #region Logistics
const LOGISTICS_COLLECTION_CACHE_KEY = 'collection_logistics';
const LOGISTICS_URL_SLUG = `logistics`;
export const clearLogisticsCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(LOGISTICS_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const getLogisticsAllocationMatrix = () => {
    const http = createDalAxiosInstance();
    return http.get(`/${LOGISTICS_URL_SLUG}/allocation-matrix`, {
        id: `${LOGISTICS_COLLECTION_CACHE_KEY}_allocation_matrix`
    });
};
export const getLogisticsMegaDeliveriesReport = (assemblyTemplateId) => {
    const http = createDalAxiosInstance();
    return http.get(`/${LOGISTICS_URL_SLUG}/mega/${assemblyTemplateId}/deliveries`, {
        id: `${LOGISTICS_COLLECTION_CACHE_KEY}_${assemblyTemplateId}_mega_deliveries`
    });
};
// #endregion
// #region NonconformanceReports (cNCR)
const NCR_COLLECTION_CACHE_KEY = 'collection_ncr';
const NCR_URL_SLUG = `documents/ncr`;
export const clearNonconformanceReportsCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(NCR_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    clearCacheByKeys(Object.keys(keys));
};
export const clearNonconformanceReportsCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + 'ncr_' + id);
};
export const getNonconformanceReport = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${NCR_URL_SLUG}/${id}`, {
        id: `ncr_${id}`
    });
};
export const getNonconformanceReports = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${NCR_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${NCR_URL_SLUG}`, {
        id,
        params
    });
};
// Create a single cNCR
export const postNonconformanceReport = (body) => {
    const http = createDalAxiosInstance();
    return http.post(`/${NCR_URL_SLUG}`, {
        nonconformanceReports: [body]
    });
};
export const putNonconformanceReport = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(NCR_COLLECTION_CACHE_KEY);
    return http.put(`/${NCR_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`ncr_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const postNcrLineItems = (documentId, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(NCR_COLLECTION_CACHE_KEY);
    return http.post(`/${NCR_URL_SLUG}/${documentId}/line-items`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putNcrLineItem = (documentId, lineItemId, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(NCR_COLLECTION_CACHE_KEY);
    return http.put(`/${NCR_URL_SLUG}/${documentId}/line-items/${lineItemId}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const deleteNcrLineItem = (documentId, lineItemId) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(NCR_COLLECTION_CACHE_KEY);
    return http.delete(`/${NCR_URL_SLUG}/${documentId}/line-items/${lineItemId}`, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const postNcrLineItemClosure = (documentId, lineItemId, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(NCR_COLLECTION_CACHE_KEY);
    return http.post(`/${NCR_URL_SLUG}/${documentId}/line-items/${lineItemId}/closure`, body, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const deleteNonconformanceReport = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(NCR_COLLECTION_CACHE_KEY);
    return http.delete(`/documents/ncr/${id}`, {
        cache: {
            update: {
                [`${NCR_COLLECTION_CACHE_KEY}`]: 'delete',
                [`ncr_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Notifications
const NOTIFICATIONS_COLLECTION_CACHE_KEY = 'collection_notifications';
const NOTIFICATIONS_URL_SLUG = `notifications`;
export const getNotifications = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${NOTIFICATIONS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${NOTIFICATIONS_URL_SLUG}`, {
        id,
        params
    });
};
export const getNotification = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${NOTIFICATIONS_URL_SLUG}/${id}`, {
        id
    });
};
export const postNotification = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(NOTIFICATIONS_COLLECTION_CACHE_KEY);
    return http.post(`/${NOTIFICATIONS_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Organizations
const ORGANIZATION_COLLECTION_CACHE_KEY = 'collection_organizations';
const ORGANIZATION_URL_SLUG = `organizations`;
export const getOrganization = (code) => {
    const http = createDalAxiosInstance();
    return http.get(`/${ORGANIZATION_URL_SLUG}/${encodeURIComponent(code)}`, {
        id: code
    });
};
export const getOrganizations = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${ORGANIZATION_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${ORGANIZATION_URL_SLUG}`, {
        id,
        params
    });
};
export const postOrganization = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ORGANIZATION_COLLECTION_CACHE_KEY);
    return http.post(`/${ORGANIZATION_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putOrganization = (code, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(ORGANIZATION_COLLECTION_CACHE_KEY);
    return http.put(`/${ORGANIZATION_URL_SLUG}/${encodeURIComponent(code)}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${code}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Outages
const OUTAGE_COLLECTION_CACHE_KEY = 'collection_outages';
const OUTAGE_URL_SLUG = `outages`;
export const deleteOutage = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(OUTAGE_COLLECTION_CACHE_KEY);
    return http.delete(`/${OUTAGE_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getOutages = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${OUTAGE_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${OUTAGE_URL_SLUG}`, {
        id,
        params
    });
};
export const postOutage = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(OUTAGE_COLLECTION_CACHE_KEY);
    return http.post(`/${OUTAGE_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putOutage = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(OUTAGE_COLLECTION_CACHE_KEY);
    return http.put(`/${OUTAGE_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Part references
const PART_REFERENCES_COLLECTION_CACHE_KEY = 'collection_part_references';
const PART_REFERENCES_URL_SLUG = `part-references`;
export const clearPartReferenceCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const deletePartReference = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(PART_REFERENCES_COLLECTION_CACHE_KEY);
    return http.delete(`/${PART_REFERENCES_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getPartReference = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${PART_REFERENCES_URL_SLUG}/${id}`, {
        id
    });
};
export const getPartReferenceReadiness = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/readiness/part-reference/${id}`, {
        id: `readiness_${id}`
    });
};
export const getPartReferences = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${PART_REFERENCES_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${PART_REFERENCES_URL_SLUG}`, {
        id,
        params
    });
};
export const postPartReference = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(PART_REFERENCES_COLLECTION_CACHE_KEY);
    return http.post(`/${PART_REFERENCES_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putPartReference = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(PART_REFERENCES_COLLECTION_CACHE_KEY);
    return http.put(`/${PART_REFERENCES_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Procedures
const PROC_COLLECTION_CACHE_KEY = 'collection_proc';
const PROC_URL_SLUG = `documents/proc`;
export const clearProcedureCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + 'proc_' + id);
};
export const clearProcedureCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(PROC_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const deleteProcedure = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(PROC_COLLECTION_CACHE_KEY);
    return http.delete(`/${PROC_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`proc_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getProcedure = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${PROC_URL_SLUG}/${id}`, {
        id: `proc_${id}`
    });
};
export const getProcedures = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${PROC_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${PROC_URL_SLUG}`, {
        id,
        params
    });
};
export const postProcedure = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(PROC_COLLECTION_CACHE_KEY);
    return http.post(`/${PROC_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putProcedure = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(PROC_COLLECTION_CACHE_KEY);
    return http.put(`/${PROC_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`proc_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Readiness
const READINESS_URL_SLUG = `readiness`;
export const clearReadinessById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + `readiness_${id}`);
};
export const getReadinessPartReference = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${READINESS_URL_SLUG}/part-reference/${id}`, {
        id: `readiness_${id}`
    });
};
export const getReadinessHardwareList = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${READINESS_URL_SLUG}/hardware-list/${id}`, {
        id: `readiness_${id}`
    });
};
// #endregion
// #region Support tickets
const SUPPORT_TICKETS_COLLECTION_CACHE_KEY = 'collection_support_tickets';
const SUPPORT_TICKETS_URL_SLUG = `support/tickets`;
export const clearSupportTicketsCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(SUPPORT_TICKETS_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const clearSupportTicketCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const getSupportTicket = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${SUPPORT_TICKETS_URL_SLUG}/${id}`, {
        id
    });
};
export const getSupportTicketAdmin = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/admin/support-tickets/${id}`, {
        id
    });
};
export const getSupportTickets = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${SUPPORT_TICKETS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${SUPPORT_TICKETS_URL_SLUG}`, {
        id,
        params
    });
};
export const getSupportTicketsAdmin = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${SUPPORT_TICKETS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/admin/support-tickets`, {
        id,
        params
    });
};
export const postSupportTicket = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(SUPPORT_TICKETS_COLLECTION_CACHE_KEY);
    return http.post(`/${SUPPORT_TICKETS_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putSupportTicket = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(SUPPORT_TICKETS_COLLECTION_CACHE_KEY);
    return http.put(`/admin/support-tickets/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Task Performance Sheets
const TPS_COLLECTION_CACHE_KEY = 'collection_tps';
const TPS_URL_SLUG = `documents/tps`;
export const clearTaskPerformanceSheetCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + 'tps_' + id);
};
export const clearTaskPerformanceSheetCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
export const closeTaskPerformanceSheet = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    return http.put(`/${TPS_URL_SLUG}/${id}/close`, {
        ...body
    }, {
        cache: {
            update: {
                [`tps_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const deleteTaskPerformanceSheet = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    return http.delete(`/${TPS_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`tps_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const deleteTaskPerformanceSheetPartReferences = (id, partReferenceIds) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    const partIds = new URLSearchParams();
    partReferenceIds.forEach((partRefId) => partIds.append('partReferenceId', partRefId));
    return http.delete(`/${TPS_URL_SLUG}/${id}/parts?${partIds}&alsoDeletePartReferenceWhenPossible=true`, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const getTaskPerformanceSheet = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${TPS_URL_SLUG}/${id}`, {
        id: `tps_${id}`
    });
};
export const getTaskPerformanceSheets = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${TPS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${TPS_URL_SLUG}`, {
        id,
        params
    });
};
export const postTaskPerformanceSheet = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    return http.post(`/${TPS_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const postCloneTaskPerformanceSheet = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    return http.post(`/${TPS_URL_SLUG}/${id}/clone`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const postPartReferenceToTaskPerformanceSheet = (id, partRequest) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    const request = {
        documentHardwareListParts: [partRequest]
    };
    return http.post(`/${TPS_URL_SLUG}/${id}/parts`, request, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const postPartReferencesToTaskPerformanceSheet = (id, partRequests) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    const request = {
        documentHardwareListParts: partRequests
    };
    return http.post(`/${TPS_URL_SLUG}/${id}/parts`, request, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putTaskPerformanceSheet = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    return http.put(`/${TPS_URL_SLUG}/${id}`, {
        ...body
    }, {
        cache: {
            update: {
                [`tps_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// initialize TPS
export const initializeTaskPerformanceSheet = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    return http.post(`/documents/tps/${id}/initialize`, {
        ...body
    }, {
        cache: {
            update: {
                [`tps_${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const reopenTaskPerformanceSheetForEdits = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TPS_COLLECTION_CACHE_KEY);
    return http.put(`/documents/tps/${id}/reopen`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
//#endregion
// #region Work Order Steps
export const clearWorkOrderStepCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(`${FRONTEND_CACHE_PREFIX + id}`);
};
export const clearWorkOrderStepCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    if (!keys) {
        return;
    }
    Object.keys(keys).forEach((key) => {
        localStorage.removeItem(key);
    });
};
const WORK_ORDER_STEPS_CACHE_KEY = 'steps_on_tps';
const WORK_ORDER_STEPS_URL_SLUG = 'work-order-steps';
export const getWorkOrderSteps = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${WORK_ORDER_STEPS_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${WORK_ORDER_STEPS_URL_SLUG}`, {
        id,
        params
    });
};
export const getWorkOrderStepsByDocumentId = (documentId) => {
    const http = createDalAxiosInstance();
    const id = `${WORK_ORDER_STEPS_CACHE_KEY}_${documentId}`;
    return http.get(`/${WORK_ORDER_STEPS_URL_SLUG}/document/${documentId}`, {
        id
    });
};
export const getWorkOrderStepById = (stepId) => {
    const http = createDalAxiosInstance();
    // const customKeyWithParams = body ? btoa(JSON.stringify(body.ids)) : '';
    const id = `${WORK_ORDER_STEPS_CACHE_KEY}_${stepId}`;
    return http.get(`/${WORK_ORDER_STEPS_URL_SLUG}/${stepId}`, {
        id,
        cache: {
            methods: ['get'],
            ttl: TTL
        }
    });
};
/**
 * Create a single step
 *
 * @param documentId string
 * @param step array of steps
 * @returns array of new steps
 */
export const postWorkOrderStep = (documentId, step) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    return http.post(`/${WORK_ORDER_STEPS_URL_SLUG}`, {
        documentId,
        steps: [step]
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const postWorkOrderSteps = (documentId, steps) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    return http.post(`/${WORK_ORDER_STEPS_URL_SLUG}`, {
        documentId,
        steps: [...steps]
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const postWorkOrderStepClosure = (id, request) => {
    const http = createDalAxiosInstance();
    return http.post(`/${WORK_ORDER_STEPS_URL_SLUG}/${id}/closure`, request);
};
export const putWorkOrderStep = (stepToUpdate) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    return http.put(`/${WORK_ORDER_STEPS_URL_SLUG}`, {
        steps: [stepToUpdate]
    }, {
        cache: {
            update: {
                [`${stepToUpdate.id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const putWorkOrderSteps = (stepsToUpdate) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    const idSpecificSpoilers = stepsToUpdate.map((step) => {
        return { [`${step.id}`]: 'delete' };
    });
    return http.put(`/${WORK_ORDER_STEPS_URL_SLUG}`, {
        steps: stepsToUpdate
    }, {
        cache: {
            update: {
                // ...idSpecificSpoilers,
                ...spoilers
            }
        }
    });
};
export const putArchiveWorkOrderStep = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    return (http.put(`/${WORK_ORDER_STEPS_URL_SLUG}/${id}/archive`),
        // no body needed
        null,
        {
            cache: {
                update: {
                    [`${id}`]: 'delete',
                    ...spoilers
                }
            }
        });
};
export const putWorkOrderStepAuthority = (stepId, authorityId, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    return http.put(`/${WORK_ORDER_STEPS_URL_SLUG}/authorities/${authorityId}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${stepId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const putWorkOrderStepAttributes = (stepId, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    return http.put(`/${WORK_ORDER_STEPS_URL_SLUG}/${stepId}/attributes`, {
        ...body
    }, {
        cache: {
            update: {
                [`${stepId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const postPartReferenceToWorkOrderStep = (id, partRequest) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    const request = {
        workOrderStepHardwareListParts: [partRequest]
    };
    return http.post(`/${WORK_ORDER_STEPS_URL_SLUG}/${id}/parts`, request, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const postPartReferencesToWorkOrderStep = (id, partRequest) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    const request = {
        workOrderStepHardwareListParts: partRequest
    };
    return http.post(`/${WORK_ORDER_STEPS_URL_SLUG}/${id}/parts`, request, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putPartReferenceOnWorkOrderStep = (stepId, partReferenceId, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    return http.put(`/${WORK_ORDER_STEPS_URL_SLUG}/${stepId}/parts/${partReferenceId}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${stepId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const deletePartReferenceFromWorkOrderStep = (stepId, partReferenceId) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    return http.delete(`/${WORK_ORDER_STEPS_URL_SLUG}/${stepId}/parts/${partReferenceId}`, {
        cache: {
            update: {
                [`${stepId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// Work Order Step Attachments
export const linkWorkOrderStepAttachment = (docId, stepId, attachmentId) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(`${DOCUMENT_ATTACHMENTS_CACHE_PREFIX}_${docId}`);
    return http.post(`/work-order-steps/${stepId}/attachments/${attachmentId}`, null, {
        cache: {
            update: {
                [`${DOCUMENT_ATTACHMENTS_CACHE_PREFIX}_${docId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const unlinkWorkOrderStepAttachment = (docId, stepId, attachmentId) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(`${DOCUMENT_ATTACHMENTS_CACHE_PREFIX}_${docId}`);
    return http.delete(`/work-order-steps/${stepId}/attachments/${attachmentId}`, {
        cache: {
            update: {
                [`${DOCUMENT_ATTACHMENTS_CACHE_PREFIX}_${docId}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Work order step form-fields
export const postFormFieldToWorkOrderStep = (id, body) => {
    const http = createDalAxiosInstance();
    // bc this returns a WorkOrderStepCollectionResponseWithRefs we need to spoil that
    const spoilers = findMatchingKeysToSpoil(WORK_ORDER_STEPS_CACHE_KEY);
    return http.post(`/${WORK_ORDER_STEPS_URL_SLUG}/${id}/form-fields`, {
        formFields: body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
// #endregion
// #region Telemetry
export const postTelemetry = (body) => {
    const TELEMETRY_CACHE_KEY = 'telemetry';
    const TELEMETRY_URL_SLUG = 'telemetry';
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(TELEMETRY_CACHE_KEY);
    return http.post(`/${TELEMETRY_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
//#endregion
// #region Tracking documents
const TRACKING_DOCS_COLLECTION_CACHE_KEY = 'collection_tracking_documents';
const TRACKING_DOCS_URL_SLUG = `documents`;
export const getTrackingDocuments = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${TRACKING_DOCS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${TRACKING_DOCS_URL_SLUG}`, {
        id,
        params
    });
};
// #endregion
// #region Transactions
const TRANSACTION_COLLECTION_CACHE_KEY = 'collection_transactions';
const TRANSACTION_URL_SLUG = `transactions`;
export const getTransactions = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${TRANSACTION_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${TRANSACTION_URL_SLUG}`, {
        id,
        params
    });
};
// #endregion
// #region Users
const USERS_COLLECTION_CACHE_KEY = 'collection_users';
const USERS_URL_SLUG = `users`;
export const clearUserCacheById = (id) => {
    if (!id) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + id);
};
export const deleteUser = (id) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(USERS_COLLECTION_CACHE_KEY);
    return http.delete(`/${USERS_URL_SLUG}/${id}`, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const getUsers = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${USERS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${USERS_URL_SLUG}`, {
        id,
        params,
        cache: {
            ttl: TTL * 10
        }
    });
};
export const getUser = (id) => {
    const http = createDalAxiosInstance();
    return http.get(`/${USERS_URL_SLUG}/${id}`, {
        id
    });
};
export const postUser = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(USERS_COLLECTION_CACHE_KEY);
    return http.post(`/${USERS_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putUserRole = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(USERS_COLLECTION_CACHE_KEY);
    return http.put(`/${USERS_URL_SLUG}/${id}/role`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
export const putUserPermissions = (id, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(USERS_COLLECTION_CACHE_KEY);
    return http.put(`/${USERS_URL_SLUG}/${id}/support-context`, {
        ...body
    }, {
        cache: {
            update: {
                [`${id}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
// #region UserGroups
const USER_GROUPS_COLLECTION_CACHE_KEY = 'collection_user_groups';
const USER_GROUPS_URL_SLUG = `user-groups`;
export const clearUserGroupCacheByCode = (code) => {
    if (!code) {
        return;
    }
    localStorage.removeItem(FRONTEND_CACHE_PREFIX + code);
};
export const clearUserGroupCollectionCache = () => {
    const keys = findMatchingKeysToSpoil(USER_GROUPS_COLLECTION_CACHE_KEY);
    if (!keys) {
        return;
    }
    clearCacheByKeys(Object.keys(keys));
};
export const getUserGroup = (code) => {
    const http = createDalAxiosInstance();
    return http.get(`/${USER_GROUPS_URL_SLUG}/${encodeURIComponent(code)}`, {
        id: code
    });
};
export const getUserGroups = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${USER_GROUPS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${USER_GROUPS_URL_SLUG}`, {
        id,
        params,
        cache: {
            ttl: TTL * 10
        }
    });
};
// This type was broken at the time of writing
export const getUserGroupsHistory = (params) => {
    const http = createDalAxiosInstance();
    const customKeyWithParams = params ? btoa(JSON.stringify(params)) : '';
    const id = `${USER_GROUPS_COLLECTION_CACHE_KEY}_${customKeyWithParams}`;
    return http.get(`/${USER_GROUPS_URL_SLUG}/history`, {
        id,
        params
    });
};
export const postUserGroup = (body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(USER_GROUPS_COLLECTION_CACHE_KEY);
    return http.post(`/${USER_GROUPS_URL_SLUG}`, {
        ...body
    }, {
        cache: {
            update: {
                ...spoilers
            }
        }
    });
};
export const putUserGroup = (code, body) => {
    const http = createDalAxiosInstance();
    const spoilers = findMatchingKeysToSpoil(USER_GROUPS_COLLECTION_CACHE_KEY);
    return http.put(`/${USER_GROUPS_URL_SLUG}/${code}`, {
        ...body
    }, {
        cache: {
            update: {
                [`${code}`]: 'delete',
                ...spoilers
            }
        }
    });
};
// #endregion
